<template>
  <div class="subject-selection-container">
    <!-- Main Content -->
    <div class="content-wrapper">
      <div class="form-section">
        <!-- Header Section -->
        <div class="section-header">
          <div class="section-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="size-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
              />
            </svg>
          </div>
          <div class="section-title">
            <h3>What subjects do you need help with? <span class="required">*</span></h3>
            <p>Select all subjects where you need tutoring support</p>
          </div>
        </div>

        <!-- Subject Selection Area -->
        <div class="form-grid">
          <div class="form-group" v-for="subject in subjects" :key="subject.id">
            <label class="subject-checkbox">
              <input type="checkbox" v-model="subject.selected" @change="updateSelectedCount" />
              <span class="checkmark"></span>
              <span class="subject-name">
                {{ subject.name }}
                <span v-if="subject.hint" class="subject-hint">{{ subject.hint }}</span>
              </span>
            </label>
          </div>
        </div>

        <!-- Footer Section -->
        <div v-if="selectedCount > 0" class="footer-section">
          <div class="selected-count">
            Selected subjects: {{ selectedCount }} of {{ subjects.length }}
          </div>
          <div class="instruction">
            You can select multiple subjects. Our tutors will help you with all your selected
            subjects.
          </div>
        </div>
      </div>

      <!-- Button Container -->
      <div class="button-container">
        <button @click="goBack" class="back-btn">
          <svg class="arrow-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41,16.58L10.83,12L15.41,7.42L14,6L8,12L14,18L15.41,16.58Z" />
          </svg>
          Back
        </button>

        <button
          @click="handleSubmit"
          :disabled="selectedCount === 0"
          class="complete-btn"
          :class="{ disabled: selectedCount === 0 }"
        >
          <span>Continue to Verification</span>
          <svg class="arrow-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useStudentRegistrationStorage } from "@/composables/useRegistrationStorage";
import { logDebug } from '@/utils/secureLogger';

// Props
interface Props {
  modelValue: {
    selectedSubjects: string[];
  };
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  "update:modelValue": [value: { selectedSubjects: string[] }];
  "subjects-complete": [value: { selectedSubjects: string[] }];
  "go-back": [];
}>();

// Subjects data
const subjects = ref([
  { id: 1, name: "Oral Communication", selected: false },
  { id: 2, name: "Komunikasyon at Pananaliksik sa Wika at Kulturang Pilipino", selected: false },
  { id: 3, name: "21st Century Literature from the Philippines and the World", selected: false },
  { id: 4, name: "Statistics and Probability", selected: false },
  { id: 5, name: "Physical Science", selected: false },
  { id: 6, name: "Reading and Writing", selected: false },
  {
    id: 7,
    name: "Pagbasa at Pagsusuri ng Iba't-Ibang Teksto Tungo sa Pananaliksik",
    selected: false,
  },
  { id: 8, name: "General Mathematics", selected: false },
  { id: 9, name: "Earth and Life Science", selected: false },
  { id: 10, name: "Earth Science", selected: false, hint: "(for STEM strand)" },
]);

// Computed selected count
const selectedCount = computed(() => {
  return subjects.value.filter((subject) => subject.selected).length;
});

// Initialize registration storage
const { saveStudentSubjects, loadStudentSubjects } = useStudentRegistrationStorage();

// Update selected count and emit changes
const updateSelectedCount = () => {
  const selectedSubjects = subjects.value
    .filter((subject) => subject.selected)
    .map((subject) => subject.name);

  emit("update:modelValue", { selectedSubjects });
  
  // Auto-save to sessionStorage
  saveStudentSubjects({ selectedSubjects });
};

// Handle form submission
const handleSubmit = () => {
  const selectedSubjects = subjects.value
    .filter((subject) => subject.selected)
    .map((subject) => subject.name);

  if (selectedSubjects.length > 0) {
    emit("subjects-complete", { selectedSubjects });
  }
};

// Handle back button
const goBack = () => {
  // Emit event to go back to previous step
  emit("go-back");
};

// Initialize with existing data if any
const initializeSubjects = () => {
  // First try to load from props
  if (props.modelValue.selectedSubjects.length > 0) {
    logDebug('Restoring subject selection from props');
    subjects.value.forEach((subject) => {
      subject.selected = props.modelValue.selectedSubjects.includes(subject.name);
    });
  } else {
    // If no props, try to load from sessionStorage
    const savedData = loadStudentSubjects();
    if (savedData && savedData.selectedSubjects.length > 0) {
      logDebug('Restoring subject selection from sessionStorage');
      subjects.value.forEach((subject) => {
        subject.selected = savedData.selectedSubjects.includes(subject.name);
      });
    }
  }
};

// Initialize on component mount
onMounted(() => {
  initializeSubjects();
});
</script>

<style scoped>
.subject-selection-container {
  min-height: 100vh;
  background: url("/student-reg-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 20px 40px;
}

.form-section {
  background: white;
  border: 3px solid #090909;
  overflow: hidden;
  border-radius: 16px;
  padding: 36px;
  margin-bottom: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 1000px;
}

/* Header Section */
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
  padding: 18px;
  padding-bottom: 18px;
  border-bottom: none;
  background-color: transparent;
  border-radius: 0;
  margin: -36px -36px 16px -36px;
}

.section-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
}

.section-icon svg {
  width: 28px;
  height: 28px;
}

.section-title h3 {
  margin: 0;
  color: #1e40af;
  font-size: 20px;
  font-weight: 600;
}

.required {
  color: #dc2626;
  font-weight: 700;
}

.section-title p {
  margin: 4px 0 0 0;
  color: #3b82f6;
  font-size: 14px;
  font-style: normal;
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 24px;
}

.form-group {
  margin-bottom: 0;
}

.subject-checkbox {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  position: relative;
  padding: 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fafafa;
  transition: all 0.2s ease;
  height: 60px;
  min-height: 60px;
  max-height: 60px;
  overflow: hidden;
}

.subject-checkbox:hover {
  border-color: #3861fb;
  background: white;
}

.subject-checkbox input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: relative;
  height: 20px;
  width: 20px;
  background-color: #eee;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.subject-checkbox:hover .checkmark {
  border-color: #3861fb;
}

.subject-checkbox input:checked ~ .checkmark {
  background-color: #3861fb;
  border-color: #3861fb;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.subject-checkbox input:checked ~ .checkmark:after {
  display: block;
}

.subject-checkbox .checkmark:after {
  left: 6px;
  top: 2px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.subject-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  line-height: 1.4;
}

.subject-hint {
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  margin-left: 6px;
}

/* Button Container */
.button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1000px;
  margin-bottom: 24px;
  gap: 16px;
}

/* Back Button */
.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: auto;
  min-width: 180px;
  padding: 18px 32px;
  background: white;
  color: #4f46e5;
  border: 3px solid #000000;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: none;
  box-shadow: 4px 4px 0px #000000;
}

.back-btn:hover {
  background: #4f46e5;
  color: white;
  transform: translateY(-2px);
  box-shadow: 6px 6px 0px #000000;
}

/* Continue Button */
.complete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: auto;
  min-width: 250px;
  padding: 18px 32px;
  background: #4f46e5;
  color: white;
  border: 3px solid #000000;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: none;
  box-shadow: 4px 4px 0px #000000;
}

.complete-btn:hover:not(:disabled) {
  background: #3730a3;
  transform: translateY(-2px);
  box-shadow: 6px 6px 0px #000000;
}

.complete-btn:disabled,
.complete-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #9ca3af;
  border-color: #6b7280;
  box-shadow: 2px 2px 0px #6b7280;
  transform: none;
}

.arrow-icon,
.check-icon {
  width: 24px;
  height: 24px;
}

/* Remove old button styles */

/* Footer Section */
.footer-section {
  margin-top: 20px;
  text-align: center;
}

.selected-count {
  font-size: 14px;
  color: #1f42b3;
  font-weight: 600;
  margin-bottom: 8px;
}

.instruction {
  font-size: 14px;
  color: #6b7280;
  font-style: italic;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .content-wrapper {
    padding: 20px 16px 40px;
  }

  .form-section {
    padding: 36px;
    margin-bottom: 28px;
  }

  .section-header {
    gap: 15px;
    margin: -36px -36px 16px -36px;
  }

  .section-icon {
    width: 48px;
    height: 48px;
  }

  .section-icon svg {
    width: 24px;
    height: 24px;
  }

  .section-title h3 {
    font-size: 20px;
  }

  .section-title p {
    font-size: 14px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 20px;
  }

  .button-container {
    justify-content: center;
    flex-direction: column-reverse;
    margin-top: 20px;
    margin-bottom: 20px;
    gap: 16px;
  }

  .back-btn {
    min-width: 180px;
    padding: 16px 28px;
    font-size: 16px;
    font-weight: 700;
  }

  .complete-btn {
    min-width: 220px;
    padding: 16px 28px;
    font-size: 16px;
    font-weight: 700;
  }

  .arrow-icon,
  .check-icon {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 480px) {
  .content-wrapper {
    padding: 16px 12px 32px;
  }

  .form-section {
    padding: 24px;
    margin-bottom: 24px;
  }

  .section-header {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    margin: -24px -24px 24px -24px;
  }

  .section-icon {
    width: 40px;
    height: 40px;
    margin: 0 auto;
  }

  .section-icon svg {
    width: 20px;
    height: 20px;
  }

  .section-title h3 {
    font-size: 18px;
  }

  .section-title p {
    font-size: 13px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 20px;
  }

  .button-container {
    justify-content: center;
    flex-direction: column-reverse;
    margin-top: 15px;
    margin-bottom: 15px;
    gap: 12px;
  }

  .back-btn {
    min-width: 160px;
    padding: 14px 24px;
    font-size: 14px;
    font-weight: 700;
  }

  .complete-btn {
    min-width: 200px;
    padding: 14px 24px;
    font-size: 14px;
    font-weight: 700;
  }

  .arrow-icon,
  .check-icon {
    width: 16px;
    height: 16px;
  }
}

.checkmark {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  background: white;
  flex-shrink: 0;
  margin-top: 2px;
  transition: all 0.2s ease;
}

.subject-checkbox input[type="checkbox"]:checked ~ .checkmark {
  background: #1e40af;
  border-color: #1e40af;
}

.subject-checkbox input[type="checkbox"]:checked ~ .checkmark::after {
  content: "";
  position: absolute;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.subject-name {
  color: #374151;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 500;
}

/* Submit Section */
.submit-section {
  padding: 32px;
  background: #dbeafe;
  display: flex;
  justify-content: center;
}

.submit-btn {
  background: #1e40af;
  color: white;
  padding: 14px 24px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  transition: background-color 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.submit-btn:hover:not(:disabled) {
  background: #1e3a8a;
}

.submit-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  color: #6b7280;
}

.arrow-icon {
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;
}

.submit-btn:hover .arrow-icon {
  transform: translateX(5px);
}

/* Footer Section */
.footer-section {
  background: #dbeafe;
  padding: 24px 32px;
}

.selected-count {
  color: #1e40af;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}

.instruction {
  color: #3b82f6;
  font-size: 14px;
  line-height: 1.4;
}

/* Responsive Design */
@media (max-width: 768px) {
  .subjects-grid {
    grid-template-columns: 1fr;
  }

  .header-section {
    padding: 24px;
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }

  .header-content h2 {
    font-size: 24px;
  }

  .subjects-area {
    padding: 24px;
  }

  .submit-section {
    padding: 24px;
  }

  .footer-section {
    padding: 20px 24px;
  }
}

@media (max-width: 480px) {
  .subject-selection-container {
    padding: 10px;
  }

  .subject-card {
    border-radius: 12px;
  }

  .header-section {
    padding: 20px;
  }

  .subjects-area {
    padding: 20px;
  }

  .submit-section {
    padding: 20px;
  }

  .footer-section {
    padding: 16px 20px;
  }
}
</style>
